/**
 * ココシル検索の条件設定フォーム画面
 *
 * * App
 *     * [KokosilForm]{@link module:KokosilForm~KokosilForm}
 *         * [KokosilFormKeyword]{@link module:KokosilForm~KokosilFormKeyword}
 *         * [KokosilFormCategories]{@link module:KokosilForm~KokosilFormCategories}
 *              * [KokosilFormCategoryChildren]{@link module:KokosilForm~KokosilFormCategoryChildren}[*]
 *                   * [KokosilFormCategoryCheckbox]{@link module:KokosilForm~KokosilFormCategoryCheckbox}
 *                   * KokosilFormCategoryChildren[*]
 *                       * KokosilFormCategoryCheckbox
 *
 * @module KokosilForm
 */
import React from 'react';
import { Row, Col, Form, Alert, Button, Spinner } from 'react-bootstrap';
import { OverlayTrigger, Popover} from 'react-bootstrap';
import { BsArrowClockwise, BsQuestionCircleFill } from "react-icons/bs";

import * as KokosilConfig from './KokosilConfig';

/**
 * kokosil の [GET /api/v3/categories]{@link https://komae-iryoukaigotiiki-map.kokosil.net/ja/developer/document/api/v3/get_categories} で取得した [Catgory]{@link https://komae-iryoukaigotiiki-map.kokosil.net/ja/developer/document/api/v3/resource/category} に、管理・制御用の属性を追加したもの。
 *
 * ここでは、表示・制御に利用している属性のみ抜粋して示す。詳細は上記リンク先の
 * ココシルのドキュメント参照のこと。
 *
 * @typedef CategoryObject
 * @prop {number} id                 - カテゴリID
 * @prop {string} name               - カテゴリ名称
 * @prop {categoryObject[]} children - 子カテゴリのArray
 * @prop {boolean} checked           - カテゴリ選択 checkbox の on/off
 * @prop {boolean} disabled          - checkbox の disabled の on/off
 *
 */

/**
 * キーワード入力 input フォームの React Component を出力
 *
 * ```js
 * // キーワード input フォームの value
 * const keyword = "search keyword";
 * // keyword 入力欄で change イベント発生時の callback
 * const handleKeywordChange = callback;
 *
 * return (
 *   <KokosilFormKeyword
 *     keyword={keyword}
 *     handleKeywordChange={handleKeywordChange}
 *   />
 * );
 * ```
 */
function KokosilFormKeyword(props) {
  return (
    <Form.Group as={Row} controlId="keyword" className="mb-3 keyword-form">
    <Col xs="auto">
    <Form.Label column>キーワード</Form.Label>
    </Col>
    <Col style={{ width: '100%', paddingLeft: 0 }}>
    <Form.Control type="text" value={props.keyword} onChange={props.handleKeywordChange} />
    </Col>
    </Form.Group>
  );
}

/**
 * カテゴリ checkbox の React Component を出力
 *
 * ```js
 * // checkbox に対応する categoryObject
 * const category = categoryObject;
 * // checkbox を disable にするか否か
 * const disabled = false;
 * // checkbox が所属する最上位のフォームグループのインデックス
 * const topIdx   = 1;
 * // checkbox の change イベント発生時の callback
 * const handleCategoryChange = callback;
 *
 * return (
 *   <KokosilFormCategoryCheckbox
 *     category={category}
 *     disabled={disabled}
 *     topIdx={topIdx}
 *     handleCategoryChange={handleCategoryChange}
 *   />
 * )
 * ```
 */
function KokosilFormCategoryCheckbox(props) {
  const cat = props.category;
  const topIdx = props.topIdx;
  const handle = props.handleCategoryChange;
  if (props.disabled) {
    return (
      <Form.Check
      type="checkbox"
      id={"id" + cat.id}
      label={cat.name}
      checked={cat.checked ? true : false}
      disabled
      data-topidx={topIdx}
      onChange={handle} />
    );
  } else {
    return (
      <Form.Check
      type="checkbox"
      id={"id" + cat.id}
      label={cat.name}
      checked={cat.checked ? true : false}
      data-topidx={topIdx}
      onChange={handle} />
    );
  }
}

/**
 * 中カテゴリ・小カテゴリの表示 React Component を出力。
 *
 * `category` で指定したカテゴリの名前とチェックボックス、および、子カテゴリが
 * 再帰で出力される。
 *
 * `handleCategoryChange` で渡された callback は [KokosilFormCategoryCheckbox]{@link module:KokosilForm~KokosilFormCategoryCheckbox} で利用される。
 *
 * ```js
 * // インデントレベル
 * // 中カテゴリは 0、小カテゴリは 1 を使っている。
 * const indent = 1;
 * // 出力するカテゴリ
 * const category = categoryObject;
 * // 無効化フラグ
 * const disabled = false;
 * // 何番目の大カテゴリに所属しているかの指定
 * const topIdx = 0;
 * // checkbox の change イベント発生時の callback
 * const handleCategoryChange = callback;
 *
 * return (
 *   <KokosilFormCategoryChildren
 *     indent={indent}
 *     category={category}
 *     disabled={disabled}
 *     topIdx={topIdx}
 *     handleCategoryChance={handleCategoryChange}
 *   />
 * );
 * ```
 */
function KokosilFormCategoryChildren(props) {
  const children = props.category.children;
  const indent = props.indent;
  const disabled = props.disabled;
  const shown = props.shown;
  const topIdx = props.topIdx;
  const handle = props.handleCategoryChange;

  if (!children || children.length === 0) {
    return null;
  }

  if (children && children.length > 0 && children[0].children.length > 0) {
    const forms = children.map((cat, idx) => {
      const children_disabled = disabled || cat.checked ? true : false;
      return (
        <Form.Group key={idx}>
        <KokosilFormCategoryCheckbox
          category={cat}
          disabled={disabled}
          topIdx={topIdx}
          handleCategoryChange={handle} />
        <KokosilFormCategoryChildren
          indent={indent+1}
          category={cat}
          disabled={children_disabled}
          topIdx={topIdx}
          handleCategoryChange={handle} />
        </Form.Group>
      );
    });
    if (shown) {
      return <div className="checkboxes">{forms}</div>;
    }
    else {
      return <div className="checkboxes hidden">{forms}</div>;
    }
  }
  else {
    const forms = children.map((cat, idx) => {
      return (
        <Col xs="auto" key={idx}>
        <KokosilFormCategoryCheckbox
          category={cat}
          disabled={disabled}
          topIdx={topIdx}
          handleCategoryChange={handle} />
        </Col>
      );
    });
    if (disabled && KokosilConfig.CATEGORY_HIDE_DISABLED) {
      return (
        <div className="checkboxes hidden">
        <Col className="cat-lv2 disabled" xs={{ offset: indent}}><Row>{forms}</Row></Col>
        </div>
      );
    }
    else {
      return (
        <div className="checkboxes">
        <Col className="cat-lv2" xs={{ offset: indent}}><Row>{forms}</Row></Col>
        </div>
      );
    }
  }
}


/**
 * 大カテゴリのカテゴリグループの React Component の出力。
 *
 * `categories` で示された大カテゴリを順に表示し、それぞれに属する中小カテゴリ
 * の選択フォームを出力する。
 *
 * `handleCategoryChange` で指定された callback は [KokosilFormCategoryChildren]{@link module:KokosilForm~KokosilFormCategoryChildren} で利用される。
 *
 * ```js
 * // 大カテゴリの Array
 * const categories = categoriesArray;
 * // 大カテゴリごとに集計した、チェックが入っている項目の数
 * // categories の n 番目の大カテゴリでの集計が
 * // `topLvCount[n]` に入る。
 * const topLvCount = [1,2,3,4,5,6,7,8,9,10,11,12];
 * // カテゴリのチェックボックスの change イベント発生時の callback
 * const handleCategoryChange = callback1;
 * // 大カテゴリのラベルをクリックして click イベント発生時の callback
 * const handleCategoryToggle = callback2;
 *
 * return (
 *   <KokosilFormCategories
 *     categories={categories}
 *     topLvCount={topLvCount}
 *     handleCategoryChange={handleCategoryChange}
 *     handleCategoryToggle={handleCategoryToggle}
 *   />
 * );
 * ```
 */
function KokosilFormCategories(props) {
  const categories = props.categories;
  const topLvCount = props.topLvCount;
  const handle     = props.handleCategoryChange;

  const toggleLv1 = ev => {
    const chkbox = ev.target;
    const label  = chkbox.parentElement;
    if (ev.type === "change") {
      label.dispatchEvent(new Event("click", { bubbles: true }));
    }
    else if (chkbox.checked === false) {
      label.dispatchEvent(new Event("click", { bubbles: true }));
    }
  };

  const forms = categories.map((cat, idx) => {
    const shown = cat.shown ? true : false;

    let disabled = false;
    let lv1shown = true;
    let className = "mb-2 category-group";
    /*
    if ( (idx === 0 && topLvCount[1] > 0) || (idx === 1 && topLvCount[0] > 0) ) {
      className += " disabled";
      disabled = true;
    }
    */

    let handle2 = props.handleCategoryToggle;
    if (KokosilConfig.CATEGORY_NO_LV1_TOGGLE) {
      handle2 = null;
    }
    if (topLvCount[idx] > 0) {
      className += " active";
      handle2 = null;
    }

    if (!shown || (KokosilConfig.CATEGORY_HIDE_DISABLED && cat.checked)) {
      className += " category-hidden";
      lv1shown = false;
    }
    else {
      className += " category-shown";
    }
    /*
    const popover = (
      <Popover id="help-popover">
        <Popover.Header as="h4">「お住まいの地域から探す」とは？</Popover.Header>
        <Popover.Body>お住まいの地域に対応している事業所を知りたい場合にご活用いただけます。</Popover.Body>
      </Popover>
    );

    const showHelpIE = () => {
      window.confirm("「お住まいの地域から探す」とは？\n====================\nお住まいの地域に対応している事業所を知りたい場合にご活用いただけます。");
    };
    */

    const firstChild = cat.children.length > 0 ? cat.children[0].id : "";
    return (
      <Form.Group key={idx} controlId={"c" + cat.id} className={className}>
      <Form.Label data-id={cat.id} onClick={handle2} className="lv1-label">
        <Form.Check
          inline
          type="checkbox"
          id={"id" + cat.id}
          className="lv1-check"
          checked={cat.checked ? true : false}
          onChange={handle}
          label={cat.name}
        />
        {!KokosilConfig.CATEGORY_NO_LV1_TOGGLE && firstChild && <><input type="checkbox" className="toggle-box" checked={lv1shown} disabled={disabled} onFocus={toggleLv1} onChange={toggleLv1} data-first-child={firstChild} /><span /></>}
        {/*
        {idx === 0 && !window.isIE && (
          // window.isIE は public/index.html 中で設定されている
          <OverlayTrigger placement="bottom" trigger="click" rootClose={true} overlay={popover}>
          <Button size="sm" variant="light" className="help-btn"><BsQuestionCircleFill /></Button>
          </OverlayTrigger>
        )}
        {idx === 0 && window.isIE && (
          <Button size="sm" variant="light" className="help-btn" onClick={showHelpIE}><BsQuestionCircleFill /></Button>
        )}
        */}
      </Form.Label>
      <KokosilFormCategoryChildren
        indent={0}
        category={cat}
        topIdx={idx}
        shown={shown}
        disabled={disabled || (cat.checked ? true : false)}
        handleCategoryChange={handle}
      />
      </Form.Group>
    );
  });
  return <>{forms}</>;
}

/**
 * ココシル検索の条件設定フォーム画面
 *
 * ```js
 * // これらは App コンポーネントからの呼び出し
 * const startSearchCallback = this.startSearch;
 * const keyword = "初期キーワード";
 * const categories = "category2=1 2&category2=3 4";
 *
 * return (
 *   <KokosilFrom
 *     startSearch={startSearchCallback}
 *     keyword={keyword}
 *     categories={categories}
 *   />
 * );
 * ```
 *
 * startSearchCallback は検索を開始するための callback。
 * [App#startSearch]{@link module:App~App#startSearch} を指定する。
 *
 * keyword はキーワードとして初期設定する文字列。
 *
 * categories は初期設定する検索カテゴリのパラメータ。
 *
 */
class KokosilForm extends React.Component {
  constructor(props) {
    super(props);
    /**
     * @member {object}
     * @prop {string} keyword           - キーワード入力フォームの値
     * @prop {CategoryObject[]} categories  - kokosil の API で取得した [CategoryObject]{@link module:KokosilForm~CategoryObject} の大カテゴリの Array。中・小カテゴリはそれぞれの大カテゴリを root とした木構造になっている。
     * @prop topLvCount {number[]} - 大カテゴリごとに集計した、チェックが入っている項目の数。categories の n 番目の大カテゴリでの集計が `topLvCount[n]` に入る。
     * @prop initError {string}         - /api/v3/categories でエラーになった場合に、エラー文字列が入る。
     */
    this.state = {
      categories: [],
      topLvCount: [],
      categoriesLoaded: false,
      initError: null,
      device: "pc",

      // 以下はパラメータ付きURLで表示したときの、キーワードやカテゴリの初期状態。
      // 「意図的に変更を無視する」ので、props を直接参照してよい。
      // https://ja.reactjs.org/docs/react-component.html の「props を state に
      // コピーしないでください。これはよくある間違いです。」を参照のこと。
      keyword: props.keyword,
      categoryParam: props.categories,
    };
  }

  /**
   * component マウント時の処理
   *
   * kokosil の GET /api/v3/categories でカテゴリ一覧を取得して
   * `this.state.categories` に格納する。
   *
   * エラーの場合は `this.state.initError` がセットされる。
   */
  componentDidMount() {
    const ua = window.navigator.userAgent.toLowerCase();
    if (ua.indexOf("kokosil") > 0) {
      this.setState({ device: "kokosil" });
    }
    else if (ua.indexOf("iphone") > 0 || ua.indexOf("android") > 0) {
      this.setState({ device: "mobile" });
    }
    else {
      this.setState({ device: "pc" });
    }

    window.fetch(
      KokosilConfig.KOKOSIL_API + `/categories?${KokosilConfig.CATEGORY_API_PARAMS}`
    ).then(res => {
        if (!res.ok) {
          throw new Error(`Network Error ${res.status} ${res.statusText}`);
        }
        return res.json();
      })
      .then(data => {
        const categories = [];
        for (let i of data["categories"]) {
          if ( KokosilConfig.CATEGORY_LV1 === null
            || KokosilConfig.CATEGORY_LV1.indexOf(i.id) !== -1) {
            categories.push(i);
          }
        }

        // LV1 カテゴリを開くか閉じるかの判定
        const topLvCount = this.state.topLvCount;
        for (let i = 0; i < categories.length; i++) {
          topLvCount[i] = 0;
        }
        if (this.state.categoryParam) {
          // クエリパラメータでカテゴリ指定がある場合
          const paramCategories = this.state.categoryParam.replace(/(category2=|&|,| )+/g, " ").trim().split(/ /);
          for (let i = 0; i < categories.length; i++) {
            const topCat = categories[i];
            if (paramCategories.includes(topCat.id.toString())) {
              topCat.checked = true;
              topCat.shown   = true;
              topLvCount[i]++;
            }
            topCat.children.forEach( cat => { // LV2 categories
              if (paramCategories.includes(cat.id.toString())) {
                cat.checked  = true;
                topCat.shown = true;
                topLvCount[i]++;
              }
              cat.children.forEach( cat => { // LV3 categories
                if (paramCategories.includes(cat.id.toString())) {
                  cat.checked  = true;
                  topCat.shown = true;
                  topLvCount[i]++;
                }
              });
            });
          }
        } else if (KokosilConfig.CATEGORY_LV1_OPEN) {
          // クエリパラメータでカテゴリ指定がない場合
          for (let i = 0; i < categories.length; i++) {
            const topCat = categories[i];
            if (KokosilConfig.CATEGORY_LV1_OPEN.indexOf(topCat.id) !== -1) {
              topCat.shown = true;
            }
          }
        }

        if (KokosilConfig.CATEGORY_NO_LV1_TOGGLE) {
          // LV1トグルが無効なので、常に開いておく。
          for (let i = 0; i < categories.length; i++) {
            categories[i].shown = true;
          }
        }

        this.setState({
          categories: categories,
          topLvCount: topLvCount,
          categoriesLoaded: true
        });
      })
      .catch(error => {
        console.error("There has been a problem with your fetch operation: ", error);
        this.setState({ initError: error.message });
      });
  }

  /**
   * カテゴリ checkbox の id に対応した CategoryObject の検索。
   * 大カテゴリのArrayから、depth first search で小カテゴリまで検索する。
   *
   * @method
   * @param {CategoryObject[]} category_list - 大カテゴリのArray
   * @param {string}           id            - checkbox の id
   * @returns {CategoryObject} 発見した Category Object、または null。
   */
  findCategory = (category_list, id) => {
    for (let i = 0; i < category_list.length; i++) {
      const cat = category_list[i];
      if ("id"+cat.id === id) {
        return cat;
      }
      if (cat.children && cat.children.length > 0) {
        const cat2 = this.findCategory(cat.children, id);
        if (cat2) {
          return cat2;
        }
      }
    }
    return;
  }

  /**
   * キーワード入力欄の更新イベント処理
   * @method
   * @param {event} ev - change イベント
   */
  handleKeywordChange = (ev) => {
    this.setState({ keyword: ev.target.value });
  }

  /**
   * カテゴリチェックボックス更新イベント処理
   * @method
   * @param {event} ev - change イベント
   */
  handleCategoryChange = (ev) => {
    const id = ev.target.id;
    const checked = ev.target.checked;
    const topIdx = parseInt(ev.target.dataset["topidx"] || "-1");
    const categories = this.state.categories;
    const topLvCount = this.state.topLvCount;

    const found = this.findCategory(categories, id);
    if (found) {
      found.checked = checked;
    }

    if (topIdx !== -1) {
      if (checked) {
        topLvCount[topIdx]++;
      }
      else {
        topLvCount[topIdx]--;
      }
    }
    /* 特定のチェックボックスが on になったときに、別のを選択できなくする */
    /*
    if (topLvCount[0] === 0 && topLvCount[1] === 0) {
      categories[0].disabled = categories[1].disabled = false;
    }
    else if (topLvCount[0] > 0) {
      categories[0].disabled = false;
      categories[1].disabled = true;
    }
    else if (topLvCount[1] > 0) {
      categories[0].disabled = true;
      categories[1].disabled = false;
    }
    */

    this.setState({ categories: categories, topLvCount: topLvCount });
  }

  /**
   * 大カテゴリ名クリックイベントによるフォームグループの開閉
   * @method
   * @param {event} ev - click イベント
   */
  handleCategoryToggle = (ev) => {
    const id = "id"+ev.target.dataset["id"];
    const categories = this.state.categories;
    const found = this.findCategory(categories, id);

    if (found) {
      found.shown = !found.shown;
    }
    this.setState({ categories: categories });
  }

  /**
   * カテゴリフォームをクリアする
   * @method
   */
  clearForm = () => {
    const categories = this.state.categories;
    const topLvCount = []
    for (let i = 0; i < categories.length; i++) {
      topLvCount[i] = 0;
    }

    function clearCategory(category_list) {
      for (let i = 0; i < category_list.length; i++) {
        const cat = category_list[i];
        cat.checked = cat.disabled = false;
        if (cat.children && cat.children.length > 0) {
          clearCategory(cat.children);
        }
      }
    }
    clearCategory(categories);

    this.setState({
      keyword: '',
      categories: categories,
      topLvCount: topLvCount
    });
  }

  /**
   * 検索ボタンクリックイベント処理（検索開始）
   * @method
   */
  doSearch = () => {
    function checkedCategories(category) {
      let checked = [];
      if (category.checked) {
        checked = [category.id];
      }
      for (let i = 0; i < category.children.length; i++) {
        checked = checked.concat( checkedCategories( category.children[i] ) );
      }
      return checked;
    }

    const keyword = this.state.keyword;
    const categories = "category2=" + this.state.categories
      .map(cat => { return checkedCategories(cat) })
      .filter(i => i.length > 0)
      .map(i => i.join("+"))
      .join("+");

    this.props.startSearch( keyword, categories, "map" );
  }

  /**
   * form の submit を無視するためのハンドラ
   * @method
   * @params {event} ev - submitイベント
   */
  ignoreSubmit = (ev) => {
    ev.preventDefault();
  }

  /**
   * render
   */
  render() {
    if (this.state.initError !== null) {
      return (
        <Form className="mt-2 mb-2">
        <Form.Group controlId="buttons">
        <Alert variant="danger">
        通信エラーが発生しました。{" "}
        <Button variant="danger" onClick={() => window.location.reload()} ><BsArrowClockwise /></Button>
        </Alert>
        </Form.Group>
        </Form>
      );
    }
    else if (!this.state.categoriesLoaded) {
      return (
        <Form className="mt-2 mb-2">
        <Form.Group controlId="buttons">
        <Spinner animation="border" size="sm" role="status" />{" "}
        読み込み中...
        </Form.Group>
        </Form>
      );
    }
    else {
      return (
        <Form className="mt-4 mb-2" onSubmit={this.ignoreSubmit}>
        {window.isIE && /* window.isIE は public/index.html 中で設定されている */ (
          <Alert variant="danger">
          Internet Explorer ではレイアウトが崩れたり、画面の反応が非常に遅くなったりします。<br/><a href="https://www.mozilla.org/ja/firefox/new/">Firefox</a>、<a href="https://www.google.co.jp/chrome/">Google Chrome</a>、<a href="https://www.microsoft.com/ja-jp/edge">Microsoft Edge</a> 等を利用してください。
          </Alert>
        )}
        <div className="about">
          <h5>キーワード・カテゴリ検索</h5>
        </div>

        <KokosilFormKeyword keyword={this.state.keyword} handleKeywordChange={this.handleKeywordChange} />
        <KokosilFormCategories
          categories={this.state.categories}
          topLvCount={this.state.topLvCount}
          handleCategoryChange={this.handleCategoryChange }
          handleCategoryToggle={this.handleCategoryToggle }
        />
        <Form.Group controlId="buttons">
        <Button variant="primary" onClick={this.doSearch}>検索</Button>{" "}
        <Button variant="secondary" onClick={this.clearForm}>条件クリア</Button>{" "}
        { this.state.device === "kokosil" &&
        <a href={ "com.uctec.kokosil.site://" + KokosilConfig.KOKOSIL_SITE_LABEL }><Button variant="info">トップに戻る</Button></a>
        }
        </Form.Group>
        </Form>
      );
    }
  }
}

export default KokosilForm;
